import {
  BODY_ROOT_KEYS,
  DENY_KEYS,
  HASH_KEYS,
  MAX_ARRAY_ITEMS,
  MAX_DEPTH,
  MAX_STRING_LENGTH,
  REDACTED,
  SAFE_KEY_PATTERNS,
  URL_KEY_PATTERN,
  VALUE_PATTERNS,
  normaliseKey,
} from './spec';
import { sanitizeUrl } from './url';
import { summariseShape } from './shape';

const matchesAny = (patterns: RegExp[], key: string): boolean => {
  const full = normaliseKey(key);
  const last = normaliseKey(key.split('.').pop() ?? key);
  return patterns.some((re) => re.test(full) || re.test(last));
};

const denyReason = (key: string): string | undefined => {
  const full = normaliseKey(key);
  const last = normaliseKey(key.split('.').pop() ?? key);
  return DENY_KEYS.find(({ re }) => re.test(full) || re.test(last))?.reason;
};

const isSafeKey = (key: string): boolean => {
  const lower = key.toLowerCase();
  return SAFE_KEY_PATTERNS.some((re) => re.test(lower));
};

/**
 * Non-cryptographic, stable, synchronous. Enough to answer "is this the same
 * search we saw a minute ago?" without carrying the search itself.
 */
function stableHash(input: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i += 1) {
    const c = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return ((h2 >>> 0).toString(16) + (h1 >>> 0).toString(16)).padStart(16, '0');
}

const truncate = (value: string): string =>
  value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…` : value;

function scrubString(value: string, counter: { hits: number }): string {
  let out = value;
  for (const { name, re } of VALUE_PATTERNS) {
    re.lastIndex = 0;
    out = out.replace(re, () => {
      counter.hits += 1;
      return REDACTED(name);
    });
  }
  return truncate(out);
}

export interface ScrubResult<T> {
  value: T;
  /** How many redactions fired. Emitted as `firmable.scrubbed_count` so the scrubber is observable. */
  scrubbed: number;
}

function walk(key: string, value: unknown, counter: { hits: number }, depth: number): unknown {
  if (value === null || value === undefined) return value;
  if (depth > MAX_DEPTH) return REDACTED('depth-limit');

  // Tier 0 first. SAFE_KEY_PATTERNS are deliberately narrow — curated
  // namespaces (firmable.*, service.*) and id/count/status shapes — so letting
  // them win opens no hole, and without this the `name` deny rule would redact
  // `service.name` and the phone regexes would eat numeric ids.
  if (isSafeKey(key)) return typeof value === 'string' ? truncate(value) : value;

  // Tier 1 — key deny-list.
  const reason = denyReason(key);
  if (reason) {
    counter.hits += 1;
    if (matchesAny(BODY_ROOT_KEYS, key)) return `[fbl-shape] ${summariseShape(value)}`;
    if (matchesAny(HASH_KEYS, key) && typeof value === 'string') {
      return `[fbl-hash:${stableHash(value)}] len=${value.length}`;
    }
    return REDACTED(reason);
  }

  // Tier 2 — payload roots are described, never carried.
  if (matchesAny(BODY_ROOT_KEYS, key) && (typeof value === 'object' || typeof value === 'string')) {
    counter.hits += 1;
    return `[fbl-shape] ${summariseShape(value)}`;
  }

  // Tier 3 — URLs keep their path, lose their query.
  if (typeof value === 'string' && URL_KEY_PATTERN.test(key)) return sanitizeUrl(value);


  if (typeof value === 'string') return scrubString(value, counter);
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return value;

  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_ITEMS).map((item, index) => walk(`${key}.${index}`, item, counter, depth + 1));
  }

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = walk(`${key}.${k}`, v, counter, depth + 1);
    }
    return out;
  }

  return REDACTED('unsupported-type');
}

/** Scrub a single attribute. `key` is the full dotted attribute name. */
export function scrubAttribute(key: string, value: unknown): ScrubResult<unknown> {
  const counter = { hits: 0 };
  const scrubbedValue = walk(key, value, counter, 0);
  return { value: scrubbedValue, scrubbed: counter.hits };
}

/** Scrub a whole attribute bag. */
export function scrubAttributes(attributes: Record<string, unknown>): ScrubResult<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  let scrubbed = 0;
  for (const [key, value] of Object.entries(attributes)) {
    const result = scrubAttribute(key, value);
    out[key] = result.value;
    scrubbed += result.scrubbed;
  }
  return { value: out, scrubbed };
}

/** Scrub free text that is not attached to a key — span names, error messages. */
export function scrubText(value: string): string {
  return scrubString(value, { hits: 0 });
}
